import React, { useContext, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { CarrierGroupListOutDTO, Pageable, PermissionTypesEnum } from '@api/ringil-api.v2';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { Dialog } from 'primereact/dialog';
import CreateOrUpdateCarrierGroup from './sidebars/CreateOrUpdateCarrierGroup';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useMediaQuery } from 'react-responsive';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface CarrierGroupsFilter {
  name: string;
}

const AllCarrierGroups = () => {
  const { tr } = useTranslationLgs();
  const { cuListCarrierGroups, cuDeleteCarrierGroup } = useContext(ApiContext);
  const [deleteCarrierGroupId, setDeleteCarrierGroupId] = useState(undefined);
  const [visibleCreateGroup, setVisibleCreateGroup] = useState(false);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);
  const { hasPermission, loggedUser } = useContext(AuthenticatedUserContext);

  const navigation = useNavigation();
  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW]);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const loadDataApi = (filter: unknown, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierGroupListOutDTO>) => void) => {
    const carrierGroupsFilter = filter as CarrierGroupsFilter;
    cuListCarrierGroups(
      pageable,
      carrierGroupsFilter?.name || '',
      data => {
        onSuccess(data);
      });
  };

  const deleteGroup = () => {
    cuDeleteCarrierGroup(deleteCarrierGroupId, () => {
      tableRef?.current?.reloadData();
      setDeleteCarrierGroupId(undefined);
    });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`AllCarriersGroupList.searchName`, `Search name`),
      value: ['name'],
    },
  ];

  const renderNameColumn = (rowData: CarrierGroupListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierGroupDetail(rowData.carrierGroupId))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {rowData.name}
            </span>
        </Link>
      </div>
    );
  };

  const renderCarrierCountColumn = (rowData: CarrierGroupListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>{rowData.carriersCount}</span>
      </div>
    );
  };

  const renderCreatedByColumn = (rowData: CarrierGroupListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>{rowData.createdBy}</span>
      </div>
    );
  };

  const renderCreatedAtColumn = (rowData: CarrierGroupListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>{stringDateTimeWithLocaleFromApiStringInTz(rowData.createdAt, loggedUser.defaultTimezoneId)}</span>
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </div>
    );
  };

  const renderActionsColumns = (rowData: CarrierGroupListOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        {editEnabled && (
          <RingilButton
            mode={'table'}
            icon={<SvgDelete />}
            onClick={() => {
              setDeleteCarrierGroupId(rowData.carrierGroupId);
            }}
          />
        )}
      </div>
    );
  };

  const columnsConfig: ColumnConfig<CarrierGroupListOutDTO>[] = [
    {
      id: 'groupName',
      header: 'AllCarriersGroupList.columnHeaderGroupName',
      headerDefault: 'Group name',
      width: '23%',
      sortable: false,
      field: 'name',
      order: 1,
      render: renderNameColumn,
    },
    {
      id: 'carriersCount',
      header: 'AllCarriersGroupList.columnHeaderNumberOfCarriers',
      headerDefault: 'Number of carriers',
      width: '23%',
      sortable: false,
      field: 'carriersCount',
      order: 2,
      render: renderCarrierCountColumn,
    },
    {
      id: 'createdBy',
      header: 'AllCarriersGroupList.columnHeaderCreatedBy',
      headerDefault: 'Created by',
      width: '23%',
      sortable: false,
      field: 'createdBy',
      order: 3,
      render: renderCreatedByColumn,
    },
    {
      id: 'createdAt',
      header: 'AllCarriersGroupList.columnHeaderCreatedAt',
      headerDefault: 'Created at',
      width: '23%',
      sortable: false,
      field: 'createdAt',
      order: 4,
      render: renderCreatedAtColumn,
    },
    {
      id: 'actions',
      width: '8%',
      sortable: false,
      field: 'actions',
      order: 5,
      render: renderActionsColumns,
    },
  ];

  return (
    <>
      <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <BreadCrumbComponent items={[{ label: tr(`AllCarriersGroupList.titleCarrierGroups`, `Carrier groups`) }]} />
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`AllCarriersGroupList.titleCarrierGroups`, `Carrier groups`)} variant={'h1'} />
          {editEnabled && (
            <div className="flex items-center">
              <RingilButton
                label={tr(`AllCarrierGroups.newCarrierGroup`, `New carrier group`)}
                icon={<SvgPlus />}
                mode={'text-only'}
                onClick={() => setVisibleCreateGroup(true)}
              />
            </div>
          )}
        </div>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'carrier_groups_list'}
      />

      {/*
        // @ts-ignore*/}
      <Dialog
        header={tr(`AllCarrierGroups.deleteThisCarrierGroup`, `Smazat tuto skupinu`)}
        visible={deleteCarrierGroupId}
        className="modal-sm"
        onHide={() => setDeleteCarrierGroupId(undefined)}
      >
        <div>
          <p
            className="p-mt-0">{tr(`AllCarrierGroups.byDeleting`, `Smazáním této skupiny budou smazány všechny navazující informace.`)}</p>
        </div>

        <ButtonLayout wrapper="WITH_PADDING_TOP_4">
          <Button label={tr(`AllCarrierGroups.delete`, `Smazat`)} className="p-mr-2 p-button-danger"
                  onClick={deleteGroup} />
          <Button label={tr(`AllCarrierGroups.cancel`, `Zrušit`)} className="p-button-text"
                  onClick={() => setDeleteCarrierGroupId(undefined)} />
        </ButtonLayout>
      </Dialog>

      <CreateOrUpdateCarrierGroup
        visible={visibleCreateGroup}
        onHide={() => setVisibleCreateGroup(false)}
        onSuccess={() => {
          tableRef?.current?.reloadData();
          setVisibleCreateGroup(false);
        }}
      />
    </>
  );
};

export default AllCarrierGroups;
